
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';
import Logo from '../base/Logo';
import ContactModal from './ContactModal';

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const navItems = [
    { path: '/', labelKey: 'nav_home' },
    { path: '/#services', labelKey: 'nav_services' },
    { path: '/#cases', labelKey: 'nav_cases' },
    { path: '/about', labelKey: 'nav_about' },
    { path: '/#contact', labelKey: 'nav_contact' },
    { path: '/apply', labelKey: 'nav_apply' },
    { path: '/articles', labelKey: 'nav_articles' }
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === path && !location.hash;
    }
    if (path.startsWith('/#')) {
      return location.hash === path.replace('/', '');
    }
    return location.pathname === path;
  };

  const scrollToSection = (sectionId: string) => {
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleNavClick = (path: string) => {
    setIsMobileMenuOpen(false);
    
    if (path === '/#contact') {
      if (location.pathname === '/') {
        scrollToSection('contact');
      } else {
        navigate('/');
        scrollToSection('contact');
      }
    } else if (path.startsWith('/#')) {
      const sectionId = path.replace('/#', '');
      if (location.pathname === '/') {
        scrollToSection(sectionId);
      } else {
        navigate('/');
        scrollToSection(sectionId);
      }
    }
  };

  const handleAboutClick = () => {
    setIsMobileMenuOpen(false);
    navigate('/about');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleApplyClick = () => {
    setIsMobileMenuOpen(false);
    navigate('/apply');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleConsultClick = () => {
    if (location.pathname === '/') {
      scrollToSection('contact');
    } else {
      navigate('/');
      scrollToSection('contact');
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'zh' ? 'en' : 'zh';
    i18n.changeLanguage(newLang);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <Logo size="medium" showText={true} />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                item.path === '/about' ? (
                  <button
                    key={item.path}
                    onClick={handleAboutClick}
                    className={`text-sm font-medium transition-colors duration-200 hover:text-blue-600 cursor-pointer ${
                      isActive(item.path)
                        ? 'text-blue-600'
                        : 'text-gray-700'
                    }`}
                  >
                    {t(item.labelKey)}
                  </button>
                ) : item.path === '/apply' ? (
                  <button
                    key={item.path}
                    onClick={handleApplyClick}
                    className={`text-sm font-medium transition-colors duration-200 hover:text-blue-600 cursor-pointer ${
                      isActive(item.path)
                        ? 'text-blue-600'
                        : 'text-gray-700'
                    }`}
                  >
                    {t(item.labelKey)}
                  </button>
                ) : item.path.startsWith('/#') ? (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`text-sm font-medium transition-colors duration-200 hover:text-blue-600 cursor-pointer ${
                      isActive(item.path)
                        ? 'text-blue-600'
                        : 'text-gray-700'
                    }`}
                  >
                    {t(item.labelKey)}
                  </button>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-sm font-medium transition-colors duration-200 hover:text-blue-600 ${
                      isActive(item.path)
                        ? 'text-blue-600'
                        : 'text-gray-700'
                    }`}
                  >
                    {t(item.labelKey)}
                  </Link>
                )
              ))}
            </nav>

            {/* Language Toggle & CTA Button */}
            <div className="hidden lg:flex items-center space-x-4">
              <button
                onClick={toggleLanguage}
                className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors duration-200 cursor-pointer"
              >
                {i18n.language === 'zh' ? 'EN' : '中文'}
              </button>
              <button
                onClick={handleConsultClick}
                className="cursor-pointer"
              >
                <Button size="sm">
                  {t('nav_consult')}
                </Button>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 cursor-pointer"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
              </div>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                {navItems.map((item) => (
                  item.path === '/about' ? (
                    <button
                      key={item.path}
                      onClick={handleAboutClick}
                      className={`text-left text-sm font-medium transition-colors duration-200 hover:text-blue-600 cursor-pointer ${
                        isActive(item.path)
                          ? 'text-blue-600'
                          : 'text-gray-700'
                      }`}
                    >
                      {t(item.labelKey)}
                    </button>
                  ) : item.path === '/apply' ? (
                    <button
                      key={item.path}
                      onClick={handleApplyClick}
                      className={`text-left text-sm font-medium transition-colors duration-200 hover:text-blue-600 cursor-pointer ${
                        isActive(item.path)
                          ? 'text-blue-600'
                          : 'text-gray-700'
                      }`}
                    >
                      {t(item.labelKey)}
                    </button>
                  ) : item.path.startsWith('/#') ? (
                    <button
                      key={item.path}
                      onClick={() => handleNavClick(item.path)}
                      className={`text-left text-sm font-medium transition-colors duration-200 hover:text-blue-600 cursor-pointer ${
                        isActive(item.path)
                          ? 'text-blue-600'
                          : 'text-gray-700'
                      }`}
                    >
                      {t(item.labelKey)}
                    </button>
                  ) : (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`text-left text-sm font-medium transition-colors duration-200 hover:text-blue-600 ${
                        isActive(item.path)
                          ? 'text-blue-600'
                          : 'text-gray-700'
                      }`}
                    >
                      {t(item.labelKey)}
                    </Link>
                  )
                ))}
                <div className="flex items-center justify-between pt-4">
                  <button
                    onClick={toggleLanguage}
                    className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors duration-200 cursor-pointer"
                  >
                    {i18n.language === 'zh' ? 'EN' : '中文'}
                  </button>
                  <button onClick={handleConsultClick} className="cursor-pointer">
                    <Button size="sm">
                      {t('nav_consult')}
                    </Button>
                  </button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </>
  );
}
